import React, { useState, useEffect } from 'react';

const DEFAULT_BRIDGE = {
  name: 'bridge1',
  comment: 'LAN Bridge',
  ports: [],
  stp_enabled: true,
  stp_priority: '0x8000',
  vlan_filtering: false,
  pvid: 1,
  auto_mac: true,
  mtu: 1500,
  vlans: [],
};

const DEFAULT_VLAN_ENTRY = { vlan_id: 10, tagged: [], untagged: [], comment: '' };

const DEFAULT_PORTS = ['ether1', 'ether2', 'ether3', 'ether4', 'ether5', 'sfp-sfpplus1'];

export default function StepBridge({ data, onChange, onNext, onBack, interfaceNames }) {
  const [bridges, setBridges] = useState(() => {
    if (data && data.length > 0) return JSON.parse(JSON.stringify(data));
    return [{ ...DEFAULT_BRIDGE }];
  });

  useEffect(() => {
    if (data && data.length > 0) {
      setBridges(JSON.parse(JSON.stringify(data)));
    }
  }, [data]);

  useEffect(() => {
    if (!data || data.length === 0) {
      onChange([{ ...DEFAULT_BRIDGE }]);
    }
  }, []);

  const ifaceList = interfaceNames && interfaceNames.length > 0 ? interfaceNames : DEFAULT_PORTS;

  const save = (updated) => {
    setBridges(updated);
    onChange(updated);
  };

  const updateBridge = (index, key, value) => {
    const updated = [...bridges];
    updated[index] = { ...updated[index], [key]: value };
    save(updated);
  };

  const togglePort = (bridgeIndex, port) => {
    const updated = [...bridges];
    const bridge = { ...updated[bridgeIndex] };
    const ports = bridge.ports || [];
    bridge.ports = ports.includes(port) ? ports.filter(p => p !== port) : [...ports, port];
    updated[bridgeIndex] = bridge;
    save(updated);
  };

  const addBridge = () => {
    const idx = bridges.length + 1;
    save([...bridges, { ...DEFAULT_BRIDGE, name: 'bridge' + idx, comment: 'Bridge-' + idx }]);
  };

  const removeBridge = (index) => {
    if (bridges.length <= 1) return;
    save(bridges.filter((_, i) => i !== index));
  };

  // VLAN functions
  const addVlanEntry = (bridgeIndex) => {
    const updated = JSON.parse(JSON.stringify(bridges));
    const bridge = updated[bridgeIndex];
    const existingVlans = (bridge.vlans || []).map(v => v.vlan_id);
    const nextId = existingVlans.length > 0 ? Math.max(...existingVlans) + 10 : 10;
    bridge.vlans = [...(bridge.vlans || []), { ...DEFAULT_VLAN_ENTRY, vlan_id: nextId }];
    save(updated);
  };

  const removeVlanEntry = (bridgeIndex, vlanIndex) => {
    const updated = JSON.parse(JSON.stringify(bridges));
    updated[bridgeIndex].vlans = (updated[bridgeIndex].vlans || []).filter((_, i) => i !== vlanIndex);
    save(updated);
  };

  const updateVlanEntry = (bridgeIndex, vlanIndex, key, value) => {
    const updated = JSON.parse(JSON.stringify(bridges));
    updated[bridgeIndex].vlans[vlanIndex] = { ...updated[bridgeIndex].vlans[vlanIndex], [key]: value };
    save(updated);
  };

  const toggleVlanPort = (bridgeIndex, vlanIndex, port, type) => {
    const updated = JSON.parse(JSON.stringify(bridges));
    const vlan = updated[bridgeIndex].vlans[vlanIndex];
    if (!vlan[type]) vlan[type] = [];
    const idx = vlan[type].indexOf(port);
    if (idx >= 0) {
      vlan[type].splice(idx, 1);
    } else {
      vlan[type].push(port);
    }
    save(updated);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🔗 Bridge (รวมพอร์ต)</h2>
        <p className="text-gray-500 dark:text-gray-400">รวมหลายพอร์ต Ethernet/SFP เข้าด้วยกันเป็นเครือข่ายเดียวกัน พร้อมตั้งค่า VLAN</p>
      </div>

      <div className="space-y-6">
        {bridges.map((bridge, bIndex) => (
          <div key={bIndex} className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-lg flex items-center justify-center text-sm font-bold shadow-md">{bIndex + 1}</span>
                <span className="font-bold text-gray-900 dark:text-white">{bridge.name}</span>
                {bridge.comment && <span className="text-xs text-gray-400">— {bridge.comment}</span>}
              </div>
              <button onClick={() => removeBridge(bIndex)} className="btn-danger" disabled={bridges.length <= 1}>ลบ</button>
            </div>

            {/* Bridge Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="label">ชื่อ Bridge</label>
                <input type="text" value={bridge.name} onChange={e => updateBridge(bIndex, 'name', e.target.value)} className="input-field" placeholder="bridge1" />
              </div>
              <div>
                <label className="label">หมายเหตุ</label>
                <input type="text" value={bridge.comment} onChange={e => updateBridge(bIndex, 'comment', e.target.value)} className="input-field" placeholder="LAN Bridge" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <label className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
                <input type="checkbox" checked={bridge.stp_enabled} onChange={e => updateBridge(bIndex, 'stp_enabled', e.target.checked)} className="w-4 h-4 text-cyan-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">STP</span>
              </label>
              <label className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
                <input type="checkbox" checked={bridge.vlan_filtering} onChange={e => updateBridge(bIndex, 'vlan_filtering', e.target.checked)} className="w-4 h-4 text-cyan-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">VLAN Filtering</span>
              </label>
              <label className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 cursor-pointer">
                <input type="checkbox" checked={bridge.auto_mac} onChange={e => updateBridge(bIndex, 'auto_mac', e.target.checked)} className="w-4 h-4 text-cyan-600 rounded" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Auto MAC</span>
              </label>
              <div>
                <label className="label text-xs">MTU</label>
                <input type="number" value={bridge.mtu} onChange={e => updateBridge(bIndex, 'mtu', parseInt(e.target.value) || 1500)} className="input-field text-sm" />
              </div>
            </div>

            {/* Port Selection */}
            <div className="mb-4">
              <label className="label">พอร์ตใน Bridge ({(bridge.ports || []).length} พอร์ต)</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {ifaceList.map(port => {
                  const isSelected = (bridge.ports || []).includes(port);
                  return (
                    <button
                      key={port}
                      onClick={() => togglePort(bIndex, port)}
                      className={`p-2.5 rounded-lg border-2 text-left text-sm transition-all ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{port}</span>
                        {isSelected ? (
                          <svg className="w-4 h-4 text-cyan-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* VLAN Configuration */}
            {bridge.vlan_filtering && (
              <div className="mt-6 p-4 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800/50 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-2">
                    <span className="text-lg">🔀</span> VLAN Configuration
                  </h3>
                  <button onClick={() => addVlanEntry(bIndex)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors">
                    + เพิ่ม VLAN
                  </button>
                </div>

                {(bridge.vlans || []).length === 0 && (
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-3">ยังไม่ได้เพิ่ม VLAN คลิกปุ่ม "+ เพิ่ม VLAN" เพื่อเริ่มต้น</p>
                )}

                <div className="space-y-3">
                  {(bridge.vlans || []).map((vlan, vIndex) => (
                    <div key={vIndex} className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-indigo-100 dark:border-indigo-800/50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-500 text-white rounded flex items-center justify-center text-xs font-bold">{vlan.vlan_id}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">VLAN {vlan.vlan_id}</span>
                          {vlan.comment && <span className="text-xs text-gray-400">— {vlan.comment}</span>}
                        </div>
                        <button onClick={() => removeVlanEntry(bIndex, vIndex)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                        <div>
                          <label className="label text-xs">VLAN ID</label>
                          <input type="number" min="1" max="4094" value={vlan.vlan_id} onChange={e => updateVlanEntry(bIndex, vIndex, 'vlan_id', parseInt(e.target.value) || 1)} className="input-field text-sm" />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="label text-xs">คำอธิบาย</label>
                          <input type="text" value={vlan.comment} onChange={e => updateVlanEntry(bIndex, vIndex, 'comment', e.target.value)} className="input-field text-sm" placeholder="เช่น VLAN Staff, VLAN Guest" />
                        </div>
                      </div>

                      {/* Tagged Ports (Trunk) */}
                      <div className="mb-2">
                        <label className="label text-xs">🔗 Tagged Ports (Trunk) — พอร์ตที่ส่ง VLAN แบบมี Tag</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                          {(bridge.ports || []).map(port => {
                            const isTagged = (vlan.tagged || []).includes(port);
                            return (
                              <button
                                key={port}
                                onClick={() => toggleVlanPort(bIndex, vIndex, port, 'tagged')}
                                className={`p-1.5 rounded text-xs transition-all ${
                                  isTagged
                                    ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700'
                                    : 'bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
                                }`}
                              >
                                {port} {isTagged ? '✓' : ''}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Untagged Ports (Access) */}
                      <div>
                        <label className="label text-xs">📡 Untagged Ports (Access) — พอร์ตที่ส่ง VLAN แบบไม่มี Tag</label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                          {(bridge.ports || []).map(port => {
                            const isUntagged = (vlan.untagged || []).includes(port);
                            return (
                              <button
                                key={port}
                                onClick={() => toggleVlanPort(bIndex, vIndex, port, 'untagged')}
                                className={`p-1.5 rounded text-xs transition-all ${
                                  isUntagged
                                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                                    : 'bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
                                }`}
                              >
                                {port} {isUntagged ? '✓' : ''}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 p-2 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg">
                  <p className="text-xs text-indigo-700 dark:text-indigo-300">
                    <strong>💡 คำอธิบาย:</strong><br/>
                    • <strong>Tagged (Trunk):</strong> พอร์ตที่ส่ง VLAN หลายๆ ตัวพร้อม tag ใช้เชื่อมระหว่าง Switch/AP<br/>
                    • <strong>Untagged (Access):</strong> พอร์ตที่ส่ง VLAN เดียวแบบไม่มี tag ใช้เชื่อม PC/Printer/อุปกรณ์ทั่วไป
                  </p>
                </div>
              </div>
            )}

            {bridge.ports && bridge.ports.length > 0 && (
              <div className="mt-3 p-2 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-800/50 rounded-lg">
                <span className="text-xs text-cyan-700 dark:text-cyan-300">
                  <strong>พอร์ตที่เลือก:</strong> {bridge.ports.join(', ')}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={addBridge} className="mt-4 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-cyan-500 hover:text-cyan-500 transition-colors w-full font-medium text-sm">
        + เพิ่ม Bridge
      </button>

      <div className="mt-4 p-3 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-100 dark:border-cyan-800/50 rounded-lg">
        <p className="text-xs text-cyan-700 dark:text-cyan-300">
          <strong>💡 เคล็ดลับ:</strong> Bridge ใช้รวมหลายพอร์ตเป็นเครือข่ายเดียวกัน เช่น รวม ether2-ether5 เป็น LAN Bridge เพื่อให้เครื่องที่เชื่อมต่อดูเหมือนอยู่ในเครือข่ายเดียวกัน
        </p>
      </div>

      <div className="flex justify-between mt-6">
        <button onClick={onBack} className="btn-secondary">← ย้อนกลับ</button>
        <button onClick={onNext} className="btn-primary">ถัดไป ({bridges.length} Bridge)</button>
      </div>
    </div>
  );
}
